/**
 * Net Exposure books — Lab `net-exposure.js` dual-call pattern.
 *
 * On load / filter change Lab fires:
 *   1. `getBooksForBackendPre` — fast inventory (market list, NE cells empty)
 *   2. `getBooksForBackend` — horse P/L + netExposure
 * Poll (~3s) refreshes only the full endpoint.
 *
 * Demo / no bet URL → fixture books (same as other console tabs).
 */

import { useQuery } from '@tanstack/react-query'
import {
  betFetch,
  getCallerAgentId,
  hasLiveApi,
  hasLiveBet,
} from '@/api/client'
import {
  backendGroupsToEvents,
  preGroupsToEvents,
  type BookEventGroupBackend,
  type BookEventGroupPre,
} from '@/api/map/books'
import { useSession } from '@/api/hooks/shared/useSession'
import { useBookmakerSessionFlag } from '@/api/hooks/shared/useStaffMe'
import { keys } from '@/api/hooks/shared/keys'
import { ROLE_LABEL, type Role } from '@/data/types'
import type { BookmakerSessionFlag } from '@/lib/bookmakerSessionFlag'
import {
  FIXTURE_EVENTS,
  type BookType,
  type NetExposureEvent,
  type SportName,
} from '@/features/net-exposure/fixture'

/** Lab polls Net Exposure every 3s (`reloadTime`). */
export const BOOKS_POLL_MS = 3_000

export type BooksQuery = {
  selectedType: BookType
  eventType: SportName
  eventname?: string
}

/** @deprecated Use BooksQuery. */
export type BooksPreQuery = BooksQuery

/** Lab `filter` + `selectedType` body (`NavigationService.getBooksFormBackend`). */
export type BooksForBackendRequest = {
  selectedType: BookType
  filter: {
    user: string
    level: string
    eventType: string
    eventname: string
    bookmakerSessionFlag?: string
  }
}

function labLevelLabel(role: Role | undefined): string {
  if (!role) return 'Master'
  return ROLE_LABEL[role]
}

function buildRequest(
  query: BooksQuery,
  user: string,
  role: Role | undefined,
  bookmakerSessionFlag: BookmakerSessionFlag,
): BooksForBackendRequest {
  return {
    selectedType: query.selectedType,
    filter: {
      user,
      level: labLevelLabel(role),
      eventType: query.eventType === 'All' ? 'All' : query.eventType,
      eventname: query.eventname?.trim() || 'All',
      bookmakerSessionFlag,
    },
  }
}

function queryKeyFields(
  query: BooksQuery,
  user: string | undefined,
  role: Role | undefined,
  bookmakerSessionFlag: BookmakerSessionFlag,
) {
  return {
    ...query,
    user: getCallerAgentId() ?? user,
    level: labLevelLabel(role),
    bookmakerSessionFlag,
  }
}

async function loadBooksPre(
  query: BooksQuery,
  accountId: string | undefined,
  role: Role | undefined,
  bookmakerSessionFlag: BookmakerSessionFlag,
): Promise<NetExposureEvent[]> {
  if (hasLiveApi()) {
    const user = getCallerAgentId() ?? accountId
    if (!hasLiveBet() || !user) return []
    const groups = await betFetch<BookEventGroupPre[]>('/v1/book/getBooksForBackendPre', {
      method: 'POST',
      body: JSON.stringify(buildRequest(query, user, role, bookmakerSessionFlag)),
    })
    return preGroupsToEvents(groups ?? [], query.eventType)
  }
  return FIXTURE_EVENTS
}

async function loadBooks(
  query: BooksQuery,
  accountId: string | undefined,
  role: Role | undefined,
  bookmakerSessionFlag: BookmakerSessionFlag,
): Promise<NetExposureEvent[]> {
  if (hasLiveApi()) {
    const user = getCallerAgentId() ?? accountId
    if (!hasLiveBet() || !user) return []
    const groups = await betFetch<BookEventGroupBackend[]>('/v1/book/getBooksForBackend', {
      method: 'POST',
      body: JSON.stringify(buildRequest(query, user, role, bookmakerSessionFlag)),
    })
    return backendGroupsToEvents(groups ?? [], query.eventType)
  }
  return FIXTURE_EVENTS
}

/** Lab `getBooksForBackendPre` — inventory only (NE cells 0). */
export function useBooksForBackendPre(query: BooksQuery) {
  const { data: session } = useSession()
  const accountId = session?.account.id
  const role = session?.account.role
  const bookmakerSessionFlag = useBookmakerSessionFlag()
  const live = hasLiveApi()

  return useQuery({
    queryKey: keys.booksPre(queryKeyFields(query, accountId, role, bookmakerSessionFlag)),
    queryFn: () => loadBooksPre(query, accountId, role, bookmakerSessionFlag),
    enabled: Boolean(session),
    // Pre is a one-shot skeleton; full books poll owns the refresh.
    staleTime: live ? BOOKS_POLL_MS : Infinity,
    placeholderData: (previous) => previous,
  })
}

/** Lab `getBooksForBackend` — horse P/L + NE; polls while live. */
export function useBooksForBackend(query: BooksQuery) {
  const { data: session } = useSession()
  const accountId = session?.account.id
  const role = session?.account.role
  const bookmakerSessionFlag = useBookmakerSessionFlag()
  const live = hasLiveApi() && hasLiveBet()

  return useQuery({
    queryKey: keys.books(queryKeyFields(query, accountId, role, bookmakerSessionFlag)),
    queryFn: () => loadBooks(query, accountId, role, bookmakerSessionFlag),
    enabled: Boolean(session),
    refetchInterval: live ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/**
 * Lab load path: fire Pre + full together; show Pre until full arrives, then
 * prefer full. Demo sessions stay on fixtures via either loader.
 */
export function useNetExposureBooks(query: BooksQuery) {
  const pre = useBooksForBackendPre(query)
  const full = useBooksForBackend(query)

  const events = full.data ?? pre.data ?? []
  const isLoading = !full.data && !pre.data && (full.isLoading || pre.isLoading)
  const isError = Boolean(full.isError && pre.isError)
  const error = full.error ?? pre.error
  const isFetching = full.isFetching || pre.isFetching

  return {
    events,
    isLoading,
    isError,
    error,
    isFetching,
    pre,
    full,
    refetch: () => {
      void pre.refetch()
      void full.refetch()
    },
  }
}
